/**
 * 원불교 법령 관리 시스템 - 인증 컨텍스트
 * 
 * 이 파일은 React Context API를 사용하여 전역 인증 상태를 관리합니다.
 * 사용자 로그인, 로그아웃, 프로필 관리, 권한 확인 등의 기능을 제공합니다.
 * 
 * 주요 기능:
 * - 사용자 인증 상태 관리
 * - 로그인/로그아웃 처리
 * - 사용자 프로필 관리
 * - 권한 기반 접근 제어
 * - 자동 토큰 검증
 * 
 * @author 원불교 법령 관리 시스템 개발팀
 * @version 1.0.0
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, UserRole, UpdateProfileRequest, UpdatePasswordRequest } from '../types';
import { authService } from '../services/authService';

/**
 * 인증 컨텍스트 타입 정의
 * 
 * 인증 관련 상태와 메서드들을 정의합니다.
 */
interface AuthContextType {
  user: User | null; // 현재 로그인한 사용자 정보
  isAuthenticated: boolean; // 인증 상태 (로그인 여부)
  isAdmin: boolean; // 관리자 권한 여부
  login: (username: string, password: string) => Promise<void>; // 로그인 함수
  register: (username: string, email: string, password: string, organ_name?: string, bubmyeng?: string) => Promise<any>; // 회원가입 함수
  updateProfile: (profileData: UpdateProfileRequest) => Promise<void>; // 프로필 수정 함수
  updatePassword: (passwordData: UpdatePasswordRequest) => Promise<void>; // 비밀번호 변경 함수
  logout: () => void; // 로그아웃 함수
  loading: boolean; // 로딩 상태
}

// 인증 컨텍스트 생성
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * AuthProvider 컴포넌트 Props 타입 정의
 */
interface AuthProviderProps {
  children: ReactNode; // 자식 컴포넌트들
}

/**
 * 인증 상태를 제공하는 Provider 컴포넌트
 * 
 * 애플리케이션 전체에서 인증 상태를 관리하고 제공합니다.
 * 
 * @component
 * @param {AuthProviderProps} props - Provider props
 * @returns {JSX.Element} 인증 컨텍스트 Provider
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null); // 현재 사용자 상태
  const [loading, setLoading] = useState(true); // 초기 로딩 상태

  // 컴포넌트 마운트 시 사용자 인증 상태 확인
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // 토큰 유효성 확인
        if (!authService.isAuthenticated()) {
          setLoading(false);
          return;
        }

        // 로컬 스토리지에서 사용자 정보 확인
        const localUser = authService.getUser();
        
        if (localUser) {
          // 로컬에 사용자 정보가 있으면 먼저 설정
          setUser(localUser);
          
          // 백그라운드에서 서버에 토큰 유효성 검증
          try {
            const serverUser = await authService.getProfile();
            // 서버에서 받은 최신 정보로 업데이트
            authService.setUser(serverUser);
            setUser(serverUser);
          } catch (error) {
            // 서버 검증 실패 시 로컬 정보는 유지하되, 토큰은 제거
            console.warn('Token validation failed, but keeping local user data');
            authService.setToken('');
          }
        } else {
          // 로컬에 사용자 정보가 없으면 서버에서 가져오기
          try {
            const serverUser = await authService.getProfile();
            authService.setUser(serverUser);
            setUser(serverUser);
          } catch (error) {
            // 서버 검증 실패 시 토큰 제거
            authService.logout();
          }
        }
      } catch (error) {
        // 인증 초기화 실패 시 토큰 제거
        authService.logout();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // 토큰 만료 이벤트 리스너 추가
    const handleTokenExpired = () => {
      setUser(null);
    };

    window.addEventListener('auth:tokenExpired', handleTokenExpired);

    // 클린업 함수
    return () => {
      window.removeEventListener('auth:tokenExpired', handleTokenExpired);
    };
  }, []);

  /**
   * 사용자 로그인 처리
   * 
   * @async
   * @param {string} username - 사용자명
   * @param {string} password - 비밀번호
   * @returns {Promise<void>}
   * @throws {Error} 로그인 실패 시 에러
   */
  const login = async (username: string, password: string) => {
    try {
      const response = await authService.login({ username, password });
      authService.setToken(response.access_token); // 토큰 저장
      authService.setUser(response.user); // 사용자 정보 저장
      setUser(response.user); // 상태 업데이트
    } catch (error) {
      // 로그인 실패 시 로컬 스토리지 정리
      authService.logout();
      throw error;
    }
  };

  /**
   * 사용자 회원가입 처리
   * 
   * @async
   * @param {string} username - 사용자명
   * @param {string} email - 이메일
   * @param {string} password - 비밀번호
   * @param {string} [organ_name] - 소속 (선택사항)
   * @param {string} [bubmyeng] - 법명 (선택사항)
   * @returns {Promise<any>} 회원가입 응답
   * @throws {Error} 회원가입 실패 시 에러
   */
  const register = async (username: string, email: string, password: string, organ_name?: string, bubmyeng?: string) => {
    try {
      const response = await authService.register({ username, email, password, organ_name, bubmyeng });
      // 회원가입 성공 후 자동 로그인하지 않음
      // 사용자가 로그인 화면에서 직접 로그인하도록 함
      return response;
    } catch (error) {
      throw error;
    }
  };

  /**
   * 사용자 프로필 수정 처리
   * 
   * @async
   * @param {UpdateProfileRequest} profileData - 수정할 프로필 데이터
   * @returns {Promise<void>}
   * @throws {Error} 프로필 수정 실패 시 에러
   */
  const updateProfile = async (profileData: UpdateProfileRequest) => {
    try {
      const updatedUser = await authService.updateProfile(profileData);
      authService.setUser(updatedUser); // 로컬 스토리지 업데이트
      setUser(updatedUser); // 상태 업데이트
    } catch (error) {
      throw error;
    }
  };

  /**
   * 사용자 비밀번호 변경 처리
   * 
   * @async
   * @param {UpdatePasswordRequest} passwordData - 비밀번호 변경 데이터
   * @returns {Promise<void>}
   * @throws {Error} 비밀번호 변경 실패 시 에러
   */
  const updatePassword = async (passwordData: UpdatePasswordRequest) => {
    try {
      await authService.updatePassword(passwordData);
    } catch (error) {
      throw error;
    }
  };

  /**
   * 사용자 로그아웃 처리
   * 
   * 로컬 스토리지를 정리하고 사용자 상태를 초기화합니다.
   */
  const logout = () => {
    authService.logout(); // 로컬 스토리지 정리
    setUser(null); // 사용자 상태 초기화
  };

  // 컨텍스트 값 객체 생성
  const value: AuthContextType = {
    user, // 현재 사용자 정보
    isAuthenticated: !!user, // 인증 상태 (사용자가 있으면 true)
    isAdmin: user?.role === UserRole.ADMIN, // 관리자 권한 여부
    login, // 로그인 함수
    register, // 회원가입 함수
    updateProfile, // 프로필 수정 함수
    updatePassword, // 비밀번호 변경 함수
    logout, // 로그아웃 함수
    loading, // 로딩 상태
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * 인증 컨텍스트를 사용하기 위한 커스텀 훅
 * 
 * 컴포넌트에서 인증 관련 상태와 메서드에 접근할 수 있도록 합니다.
 * 
 * @returns {AuthContextType} 인증 컨텍스트 값
 * @throws {Error} AuthProvider 외부에서 사용할 경우 에러
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 